/** @prettier */

import {Box, Stack} from '@mui/material';
import {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useLoaderData, useParams} from 'react-router-dom';

//MTH component import
import OUSetting from './OUSetting';
import OtherUnitSetting from '../sl-other-registration/OtherUnitSetting';
import GWConfigUpdateDrawer from '../gw-registration/GWConfigUpdateDrawer';
//API import
import {
  getCompositionsGateway,
  getCompositionsSLUnit,
  getCompositionsSLIFUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {useOutlet} from '../gw-registration/GWMain';
import {gwInfoSort, slInfoSort, sortByUnicode} from '../../functions/sort';
import {checkUnitExistenceByGatewayID} from '../../functions/checkUnitExistence';

type loaderReturn = {
  /** サイトに登録されているゲートウェイの情報 */
  allGWInfo: apiIF.res.getCompositionsGateway;
  /** 表示しているゲートウェイが課金しているかどうか */
  isBilling: boolean;
  /** 表示しているゲートウェイに登録されている室内外機の情報 */
  slInfo: apiIF.res.getCompositionsSLUnit;
  /** 表示しているゲートウェイに登録されているその他空調機の情報 */
  slOtherInfo: apiIF.res.getCompositionsSLIFUnit;
  /** 登録ユニットの有無 */
  hasUnit: boolean;
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<loaderReturn | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined || params.gwID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //ゲートウェイの情報を取得
    promises.push(getCompositionsGateway(params.siteID, {'max-result': 100}));
    //室内外機の情報を取得
    promises.push(
      getCompositionsSLUnit(params.siteID, params.gwID, {'max-result': 400})
    );
    //その他空調機の情報を取得
    promises.push(
      getCompositionsSLIFUnit(params.siteID, params.gwID, {'max-result': 400})
    );
    // ゲートウェイの機器登録状況を取得
    promises.push(checkUnitExistenceByGatewayID(params.siteID, params.gwID));
    const response = await Promise.all(promises);

    const allGWInfo = response[0] as apiIF.res.getCompositionsGateway;
    const slInfo = response[1] as apiIF.res.getCompositionsSLUnit;
    const slOtherInfo = response[2] as apiIF.res.getCompositionsSLIFUnit;
    /** ユニットの登録状況 */
    const unitExistence = response[3] as boolean;

    //課金状況の判定
    let isBilling = false;
    const displayGWInfo = allGWInfo['gateway-informations'].find(gwInfo => {
      return gwInfo['gateway-id'] === params.gwID;
    });
    if (
      displayGWInfo !== undefined &&
      displayGWInfo['billing-status'] !== undefined
    ) {
      isBilling = displayGWInfo['billing-status'] !== 'unpaid';
    }

    //表示内容をUnicode順に並び替え
    slOtherInfo['unit-informations'].sort(sortByUnicode('unit-name'));
    return {
      allGWInfo: gwInfoSort(allGWInfo),
      isBilling: isBilling,
      slInfo: slInfoSort(slInfo),
      slOtherInfo: slOtherInfo,
      hasUnit: unitExistence as boolean,
    };
  } catch {
    return undefined;
  }
}

/**
 * SL通信設定の室外機一覧
 * @returns JSX.Element
 */
const OURegistration = () => {
  const trans = useTranslation().t; //翻訳関数
  const loaderData = useLoaderData() as loaderReturn | undefined;
  //パスパラメータ取得
  const {siteID, gwID, systemType: slSystem} = useParams();
  //親コンポーネントから値を取得
  const {setAllGWInfo} = useOutlet();

  const [isBilling, setIsBilling] = useState(false); //表示しているゲートウェイが課金しているかどうか
  //prettier-ignore
  const [slInfo, setSLInfo] = useState<apiIF.res.getCompositionsSLUnit | null>(null); //室内外機の情報
  //prettier-ignore
  const [slOtherInfo, setSLOtherInfo] = useState<apiIF.res.getCompositionsSLIFUnit | null>(null); //その他空調機の情報
  const [hasConfigGW, setHasConfigGW] = useState<boolean>(false); //GW毎の構成情報未反映の有無
  const [hasUnit, setHasUnit] = useState<boolean>(false); //GWの機器登録有無
  const [slType, setSLType] = useState<'new' | 'old'>('new'); //SL新旧

  //SLタイプ変換テーブル
  const slSystemTable: {[key: string]: string} = {
    sl1: trans('OURegistration.superlinkUnit.title.superlink1'),
    sl2: trans('OURegistration.superlinkUnit.title.superlink2'),
  };

  useEffect(() => {
    if (loaderData !== undefined) {
      setAllGWInfo(loaderData.allGWInfo);
      setIsBilling(loaderData.isBilling);
      setSLInfo(loaderData.slInfo);
      setSLOtherInfo(loaderData.slOtherInfo);
      setHasUnit(loaderData.hasUnit);

      //構成情報未反映の有無・SL新旧をチェック
      for (const gwInfo of loaderData.allGWInfo['gateway-informations']) {
        if (gwInfo['gateway-id'] !== gwID) continue;
        setHasConfigGW(
          loaderData.isBilling &&
            gwInfo['composition-receipt-version'] !== undefined &&
            gwInfo['composition-receipt-version'] !== 'latest'
        );
        if (gwInfo['sl-type'] !== undefined && gwInfo['sl-type'] !== '') {
          setSLType(gwInfo['sl-type']);
        }
        break;
      }
    }
  }, [loaderData, gwID, setAllGWInfo]);

  //空調機の情報が更新された場合、ツリービューを更新
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    //ゲートウェイの情報を取得
    getCompositionsGateway(siteID, {'max-result': 100}).then(response => {
      //表示内容をUnicode順に並び替え
      response = gwInfoSort(response);
      setAllGWInfo(response);
      //ユニットの有無をチェック
      for (const gwInfo of response['gateway-informations']) {
        if (gwInfo['gateway-id'] !== gwID) continue;
        checkUnitExistenceByGatewayID(siteID, gwID).then(response => {
          setHasUnit(response as boolean);
        });
        break;
      }
    });
  }, [slInfo, siteID, setAllGWInfo]);

  //パスパラメータを取得できない場合
  if (slSystem === undefined) {
    return <></>;
  }
  return (
    <>
      <Box className="gwSetting__title">
        {/* SL系統名称 */}
        {slSystemTable[slSystem]}
      </Box>
      <Stack spacing={4}>
        {/* 登録済み室外機 */}
        <OUSetting
          slInfo={slInfo}
          setSLInfo={setSLInfo}
          isBilling={isBilling}
          setHasConfigGW={setHasConfigGW}
          slType={slType}
        />
        {/* その他室内機設定 */}
        <OtherUnitSetting
          slOtherInfo={slOtherInfo}
          setSLOtherInfo={setSLOtherInfo}
        />
      </Stack>
      {/* 構成情報反映ボタン */}
      <GWConfigUpdateDrawer
        hasConfigGW={hasConfigGW}
        setHasConfigGW={setHasConfigGW}
        hasUnit={hasUnit}
      />
    </>
  );
};
export default OURegistration;
